import { useState } from "react"
import AvatarStack from "@/components/AvatarStack/AvatarStack"
import CardFace from "@/components/CardFace/CardFace"
import { cardIdsForFamily } from "@/content/cards"
import { CARDS_COPY, DECK_BROWSER_COPY } from "@/content/cardsRound"
import { familiesInPlay } from "@/content/families"
import type { StackPerson } from "@/components/AvatarStack/AvatarStack"
import type { CardId } from "@/types"
import styles from "@/styles/components/DeckSpread.module.css"

/*
 * The whole deck as one scroll, sectioned by family.
 *
 * Two callers, deliberately one component. In the deck browser it is a read; in
 * the Cards round it is how someone revises a hand they built by dealing. The
 * cards have to be the same object in both, or the spread stops being the place
 * you go to change your mind.
 *
 * Pick mode is `onToggle`. Read mode is `onOpen`. Passing neither is a static
 * spread, which is what the report and any future print view want.
 *
 * Flip state is per card and local: which card someone has turned over is not
 * anybody else's business, and it should not survive a re-render of the room.
 */

interface DeckSpreadProps {
  families: number[]
  /** Read mode: bring this card up on its own. */
  onOpen?: (cardId: CardId) => void
  /** Pick mode: this browser's hand, and the write that changes it. */
  picked?: readonly CardId[]
  onToggle?: (cardId: CardId) => void
  /** Hand is full, so untaken cards cannot be taken. */
  capReached?: boolean
  /** Who else has picked it. Facilitator only — the room's picks stay private. */
  pickersFor?: (cardId: CardId) => readonly StackPerson[]
}

export default function DeckSpread({
  families,
  onOpen,
  picked,
  onToggle,
  capReached,
  pickersFor,
}: DeckSpreadProps) {
  const [flipped, setFlipped] = useState<CardId | null>(null)

  const inPlay = familiesInPlay(families)

  return (
    <div className={styles.spread}>
      {/*
       * A jump bar rather than pagination: the room is told "family three", and
       * six sections of four cards is a long scroll to hunt through.
       */}
      <nav className={styles.jumps} aria-label="Card families">
        {inPlay.map((family) => {
          const held = picked
            ? cardIdsForFamily(family.n).filter((id) => picked.includes(id)).length
            : 0
          return (
            <a
              key={family.n}
              className={styles.jump}
              href={`#family-${family.n}`}
              title={family.name}
            >
              <span
                className={styles.dot}
                style={{ background: `var(--family-${family.n}-bloom)` }}
                aria-hidden="true"
              />
              <span>{family.short}</span>
              {held > 0 ? <span className={styles.jumpCount}>{held}</span> : null}
            </a>
          )
        })}
      </nav>

      {inPlay.map((family) => (
        <section key={family.n} className={styles.family} id={`family-${family.n}`}>
          <header className={styles.familyHead}>
            <span
              className={styles.familyRule}
              style={{ background: `var(--family-${family.n}-bloom)` }}
              aria-hidden="true"
            />
            <div>
              <h2 className={styles.familyName}>{family.name}</h2>
              <p className={styles.familyBlurb}>{family.blurb}</p>
            </div>
          </header>

          <div className={styles.cards}>
            {cardIdsForFamily(family.n).map((cardId) => {
              const held = picked?.includes(cardId) ?? false
              const pickers = pickersFor?.(cardId) ?? []

              return (
                <div key={cardId} className={styles.slot}>
                  <CardFace
                    cardId={cardId}
                    side={flipped === cardId ? "back" : "front"}
                    onFlip={() => setFlipped((current) => (current === cardId ? null : cardId))}
                    className={held ? styles.heldCard : undefined}
                  />

                  {onToggle ? (
                    <button
                      type="button"
                      className={held ? `${styles.take} ${styles.taken}` : styles.take}
                      // A full hand blocks taking, never dropping — otherwise the
                      // only way out of a full hand would be to reload.
                      disabled={capReached && !held}
                      onClick={() => onToggle(cardId)}
                    >
                      {held ? CARDS_COPY.drop : capReached ? CARDS_COPY.takeFull : CARDS_COPY.take}
                    </button>
                  ) : onOpen ? (
                    <button type="button" className={styles.take} onClick={() => onOpen(cardId)}>
                      {DECK_BROWSER_COPY.whatThisMeans}
                    </button>
                  ) : null}

                  {/*
                    The facilitator's read on the spread: who nominated this and
                    how many. They can still pick themselves — unlike the
                    prototype, where the facilitator's button was replaced by the
                    count — so the tally sits beside the avatars rather than
                    taking the button's place.
                  */}
                  {pickersFor ? (
                    <span className={styles.pickers}>
                      {pickers.length > 0 ? (
                        <AvatarStack people={pickers} size={28} max={5} />
                      ) : null}
                      <span className={styles.pickerCount}>
                        {CARDS_COPY.nominations(pickers.length)}
                      </span>
                    </span>
                  ) : null}
                </div>
              )
            })}
          </div>
        </section>
      ))}
    </div>
  )
}
